import { GOOGLE_OAUTH_CALLBACK, SLACK_OAUTH_CALLBACK } from '@/utils/constants';

import { FORM_CREATE_ROUTE, FORM_PLAYGROUND_ROUTE } from '../utils/server/constants';
import { QUERY_PARAMS } from './query-params';

export const APP_ROUTES = {
	ROOT: '/',
	LANDING: '/landing',
	HOME: '/home',
	MESSAGES: '/messages',
	SUBMISSIONS: '/applications',
	FORM_DETAILS: '/forms/:id/responses',
	FORM_EDIT: '/forms/:id/edit',
	LOGIN: '/login',
	LOGOUT: '/logout',
	GOOGLE_OAUTH: '/google-oauth',
	GOOGLE_OAUTH_CALLBACK: '/google-oauth/callback',
	GOOGLE_OAUTH_ERROR: '',
	META_IMAGE: '/meta-image',
	FORM_FILL: '/:id',
	WORKSPACE_HANDLE: '/:id',
	PRIVACY: '/privacy',
	TERMS: '/terms',
	GDPR: '/gdpr',
	TEMPLATE: '/template/:id',
	PRIVATE_MEDIA: '/private/media',
	PRICING: '/pricing',
	PLANS: '/plans',
	BILLING: '/settings/billing',
	PAYMENT_SUCCESS: '/payment/success',
	PAYMENT_CANCEL: '/payment/cancel',
	NOT_FOUND: '/404',
	SETTINGS: '/settings',
	CANDIDATES_PAGE: '/candidates',
	WORKSPACE_OPS: '/workspace-ops',
	IMPORT_FORM: '/import-form',
	IMPORT_SUBMISSIONS: '/forms/:id/import-submissions',
	DISCOVER: '/discover',
	DISCOVER_ADMIN: '/discover/admin',
	CAREERS: '/:handle',
	PAGE_EDIT: '/workspaces/:handle/edit',
	FORM_PLAYGROUND: '/form-playground',
	ANALYTICS: '/analytics',
	SLACK_OAUTH: '/slack-oauth',
	SLACK_OAUTH_CALLBACK: '/slack-oauth/callback',
	REFERRALS: '/refer',
	REFERRAL_INVITE: '/:handle/refer',
	INTERVIEW_FEEDBACK: '/interview/:token',
	INTERVIEW_FEEDBACK_LEGACY: '/interview-feedback/:token',
	FREE_ATS: '/free-ats',
	COMPARE: '/compare',
	BEST_FREE_ATS: '/resources/best-free-ats',
	AI_SCREENING: '/features/ai-screening',
	MCP: '/mcp',
	AI_ATS: '/ai-ats',
	SWITCH: '/switch',
	ATS_WITH_MCP: '/resources/ats-with-mcp',
	SCREEN_APPLICATION_EMAILS: '/resources/screen-job-emails',
	SCREEN_GOOGLE_FORMS: '/resources/screen-google-forms',
	SCHEDULING_LINK_BOOKING: '/schedule/:token',
	ASSIGNMENT: '/assignment/:token',
	OFFER: '/offer/:token',
	OFFER_TEMPLATE_EDIT: '/offer-templates/:id',
	AI_AGENT: '/ai',
	DOCS: '/docs',
	CHANGELOG: '/changelog',
	OAUTH_CONSENT: '/oauth/consent',
	CHANGELOG_EDIT: '/edit/changelog',
};

export const PROTECTED_ROUTES = [
	APP_ROUTES.HOME,
	APP_ROUTES.MESSAGES,
	APP_ROUTES.SUBMISSIONS,
	APP_ROUTES.FORM_DETAILS,
	APP_ROUTES.FORM_EDIT,
	APP_ROUTES.PRIVATE_MEDIA,
	APP_ROUTES.PLANS,
	APP_ROUTES.BILLING,
	APP_ROUTES.PAYMENT_SUCCESS,
	APP_ROUTES.PAYMENT_CANCEL,
	APP_ROUTES.IMPORT_FORM,
	APP_ROUTES.IMPORT_SUBMISSIONS,
	APP_ROUTES.ANALYTICS,
	APP_ROUTES.INTERVIEW_FEEDBACK,
	APP_ROUTES.INTERVIEW_FEEDBACK_LEGACY,
	APP_ROUTES.PAGE_EDIT,
	APP_ROUTES.REFERRALS,
	APP_ROUTES.REFERRAL_INVITE,
	APP_ROUTES.AI_AGENT,
	APP_ROUTES.ASSIGNMENT,
	APP_ROUTES.OAUTH_CONSENT,
	APP_ROUTES.OFFER_TEMPLATE_EDIT,
];

export const PUBLIC_ROUTES = [
	APP_ROUTES.GOOGLE_OAUTH,
	APP_ROUTES.GOOGLE_OAUTH_ERROR,
	GOOGLE_OAUTH_CALLBACK,
	APP_ROUTES.SLACK_OAUTH,
	SLACK_OAUTH_CALLBACK,
	APP_ROUTES.LOGIN,
	APP_ROUTES.LOGOUT,
	APP_ROUTES.META_IMAGE,
	APP_ROUTES.PRIVACY,
	APP_ROUTES.TERMS,
	APP_ROUTES.GDPR,
	APP_ROUTES.TEMPLATE,
	APP_ROUTES.PRICING,
	APP_ROUTES.NOT_FOUND,
	APP_ROUTES.DISCOVER,
	APP_ROUTES.CAREERS,
	APP_ROUTES.SCHEDULING_LINK_BOOKING,
	APP_ROUTES.OFFER,
];

export const UNRESTRICTED_ROUTES = [
	APP_ROUTES.ROOT,
	APP_ROUTES.LOGOUT,
	APP_ROUTES.GOOGLE_OAUTH_CALLBACK,
	APP_ROUTES.SLACK_OAUTH,
	APP_ROUTES.SLACK_OAUTH_CALLBACK,
	APP_ROUTES.META_IMAGE,
	APP_ROUTES.FORM_FILL,
	APP_ROUTES.PRIVACY,
	APP_ROUTES.TERMS,
	APP_ROUTES.GDPR,
	APP_ROUTES.TEMPLATE,
	APP_ROUTES.PRICING,
	APP_ROUTES.DISCOVER,
	APP_ROUTES.CAREERS,
	APP_ROUTES.FREE_ATS,
	APP_ROUTES.SCHEDULING_LINK_BOOKING,
	APP_ROUTES.OFFER,
];

// add the routes which are part of main routes but without dashboard layout
export const PROTECTED_ROUTES_WITHOUT_DASHBOARD_LAYOUT = [
	APP_ROUTES.FORM_EDIT,
	FORM_CREATE_ROUTE,
	FORM_PLAYGROUND_ROUTE,
	APP_ROUTES.FORM_DETAILS,
	APP_ROUTES.WORKSPACE_OPS,
	APP_ROUTES.IMPORT_FORM,
	APP_ROUTES.IMPORT_SUBMISSIONS,
	APP_ROUTES.INTERVIEW_FEEDBACK,
	APP_ROUTES.INTERVIEW_FEEDBACK_LEGACY,
	APP_ROUTES.PAGE_EDIT,
	APP_ROUTES.REFERRAL_INVITE,
	APP_ROUTES.OFFER_TEMPLATE_EDIT,
];

export enum APP_MENU_ITEMS {
	HOME = 'Home',
	MESSAGES = 'Inbox',
	CANDIDATES = 'Candidates',
	ANALYTICS = 'Analytics',
	REFERRALS = 'Referrals',
	AI_AGENT = 'Assistant',
}

export const MIXPANEL_SESSION_REPLAYS_BLOCKED_ROUTES = [
	APP_ROUTES.FORM_FILL,
	APP_ROUTES.HOME,
	APP_ROUTES.NOT_FOUND,
	APP_ROUTES.PRICING,
	APP_ROUTES.TERMS,
	APP_ROUTES.META_IMAGE,
];

export const INTERCOM_ENABLED_ROUTES = [APP_ROUTES.ROOT, APP_ROUTES.PRICING, APP_ROUTES.PLANS];

// Routes where sidebar should be in icon (collapsed) mode
export const SIDEBAR_ICON_MODE_ROUTES = [APP_ROUTES.MESSAGES, APP_ROUTES.CANDIDATES_PAGE];

// Routes that a REFERRER role user is allowed to access; all other protected routes redirect to REFERRALS
// AI_AGENT is allowed so the onboarding guard (which parks nameless users on /ai) never fights this guard.
export const REFERRER_ALLOWED_ROUTES = [
	APP_ROUTES.REFERRALS,
	APP_ROUTES.REFERRAL_INVITE,
	APP_ROUTES.INTERVIEW_FEEDBACK,
	APP_ROUTES.INTERVIEW_FEEDBACK_LEGACY,
	APP_ROUTES.LOGIN,
	APP_ROUTES.AI_AGENT,
];

// Tokenized deep-link surfaces the onboarding guard must not hijack — bouncing these
// to /ai would discard the token URL (interview feedback, referral invites).
export const ONBOARDING_GUARD_EXEMPT_ROUTES = [
	APP_ROUTES.AI_AGENT,
	APP_ROUTES.INTERVIEW_FEEDBACK,
	APP_ROUTES.INTERVIEW_FEEDBACK_LEGACY,
	APP_ROUTES.REFERRAL_INVITE,
];

// ─── AI Agent Route Config ────────────────────────────────────────────────

/**
 * Configuration for routes where the AI agent panel is available.
 * `navItem` marks pages that should also appear as `@` suggestions in the agent input.
 */
export type AgentRouteConfig = {
	route: string;
	navItem: { token: string; label: string };
};

export const AGENT_ALLOWED_ROUTES: AgentRouteConfig[] = [
	{ route: APP_ROUTES.ROOT, navItem: { token: 'root', label: 'Home' } },
	{ route: APP_ROUTES.HOME, navItem: { token: 'home', label: 'Home' } },
	{ route: APP_ROUTES.FORM_DETAILS, navItem: { token: 'form', label: 'Current form' } },
	{ route: APP_ROUTES.FORM_EDIT, navItem: { token: 'form', label: 'Current form' } },
	{ route: APP_ROUTES.SUBMISSIONS, navItem: { token: 'candidates', label: 'Candidates' } },
	{ route: APP_ROUTES.ANALYTICS, navItem: { token: 'analytics', label: 'Analytics' } },
	{ route: APP_ROUTES.SETTINGS, navItem: { token: 'settings', label: 'Settings' } },
	{ route: APP_ROUTES.PRICING, navItem: { token: 'pricing', label: 'Pricing' } },
	{ route: APP_ROUTES.CANDIDATES_PAGE, navItem: { token: 'candidates', label: 'Candidates' } },
	{ route: APP_ROUTES.PAGE_EDIT, navItem: { token: 'workspace', label: 'Current workspace' } },
];

/** Returns true if the AI agent panel should be shown on the given pathname. */
export function isAgentAllowedOnRoute(pathname: string): boolean {
	return AGENT_ALLOWED_ROUTES.some(({ route }) => matchAppRoute(route, pathname));
}

export const getFilledAppRoute = (route: string, params: Record<string, string>) => {
	if (!Object.values(APP_ROUTES)?.includes(route)) return route;

	return Object.entries(params).reduce((acc, [key, value]) => {
		return acc.replace(`:${key}`, value);
	}, route);
};

/** Route to a single submission open in its form's responses table. Canonical
 *  builder for the `FORM_DETAILS?submissionId=` pattern used across surfaces. */
export const getFormResponseSubmissionRoute = (formId: string, submissionId: string) =>
	`${getFilledAppRoute(APP_ROUTES.FORM_DETAILS, { id: formId })}?${
		QUERY_PARAMS.SUBMISSION_ID
	}=${encodeURIComponent(submissionId)}`;

/**
 * Tests if a path matches a route pattern with parameters
 * Converts route patterns (e.g. "/user/:id") to regex
 * Useful for route matching in client-side navigation
 * @param routePath - Route pattern with parameter placeholders
 * @param currentPath - Actual path to test against pattern
 * @returns True if path matches pattern, false otherwise
 */
export function matchAppRoute(routePath: string, currentPath: string) {
	const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
	const regex = new RegExp(`^${pattern}$`);
	return regex.test(currentPath);
}
